<script>
import {useCollectionStore} from "@js/store/CollectionStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import {Link} from "@inertiajs/vue3";

export default {
    name: "CollectionInfoBlock",
    components: {Link, NumberFormatComponent},
    props: {
        collection: {
            required: true,
        }
    },
    methods: {
        scrollToProducts() {
            const el = document.getElementById("products_list");
            if (!el) return;

            const topPos = el.getBoundingClientRect().top + window.scrollY;

            const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
            const offset = isDesktop ? 180 : 20;

            window.scrollTo({
                top: topPos - offset,
                behavior: "smooth",
            });
        }
    },
    setup() {
        const collectionStore = useCollectionStore()

        return {collectionStore}
    }
}
</script>

<template>
    <div class="bg-grayBg p-6 rounded-2xl shadow">
        <div class="flex justify-between items-start">
            <div>
                <div class="text-xs font-medium uppercase text-gray-500">Коллекция</div>
                <h1 class="mt-1 text-2xl font-semibold text-gray-900">
                    {{ collection.category.title }}
                    {{ collection.title }}
                </h1>
            </div>
            <div class="flex items-center">
                <img v-if="collection.brand.country_code"
                     :src="'/assets/flags/' + collection.brand.country_code + '.svg'"
                     :title="collection.brand.country_code"
                     :alt="collection.brand.country_code"
                     class="h-5 w-6 rounded object-cover"/>
            </div>
        </div>
        <div class="mt-2 flex items-center text-sm text-gray-600">
            <div class="font-medium">
                от
                <Link :href="route('catalog', {
                                                 category_title:collectionStore.collection.category.slug,
                                                 brands: JSON.stringify([collection.brand.id])
                                                 })"
                      class="md:hover:text-navHoverColor transition">
                    {{ collection.brand.title }}
                </Link>
            </div>
        </div>
        <div  v-if="!collectionStore.loadings.infoLoading" class="mt-6 flex flex-col md:flex-row md:items-center md:justify-between md:space-x-4 max-md:gap-5">
            <div class="text-xl font-bold">
                от
                <NumberFormatComponent :number="collectionStore.minPrice"/>
                руб./м<sup>2</sup>
            </div>
            <button type="button" @click.prevent="scrollToProducts"
                    class="bg-baseColor text-black px-5 py-3 rounded-xl text-sm font-semibold uppercase md:hover:bg-baseColor/80">
                Перейти к товарам
            </button>
        </div>
        <div v-else class="mt-6 flex flex-col md:flex-row md:items-center md:justify-between md:space-x-4 max-md:gap-5 animate-pulse">
            <div class="h-6 bg-gray-300 rounded w-40"></div>
            <div class="h-10 bg-gray-300 rounded w-40 mt-4 md:mt-0"></div>
        </div>
        <hr class="my-6 border-gray-200"/>
        <!--        {{collectionStore.products}}-->
        <div  v-if="!collectionStore.loadings.infoLoading"
              class="grid grid-cols-12 gap-3 text-xs font-medium text-gray-700">
            <template v-if="collectionStore.sizeTitles">
                <div class="col-span-6 md:col-span-4">
                    Основные размеры
                </div>
                <div class="col-span-6 md:col-span-8">
                    {{ collectionStore.sizeTitles }}
                </div>
            </template>
            <template v-if="collectionStore.surfaces">
                <div class="col-span-6 md:col-span-4">
                    Поверхность
                </div>
                <div class="col-span-6 md:col-span-8">
                    <template v-for="(item, key) in collectionStore.surfaces">
                        <Link :href="route('catalog', {
                                                 category_title:collectionStore.collection.category.slug,
                                                 surfaces: JSON.stringify([item.id]),
                                                 })"
                              class="hover:text-navHoverColor transition underline underline-offset-4">
                            {{ item.title }}
                        </Link>
                        <span v-if="key < Object.keys(collectionStore.surfaces).length - 1"> | </span>
                    </template>
                </div>
            </template>
            <template v-if="collectionStore.colors">
                <div class="col-span-6 md:col-span-4">
                    Цвета
                </div>
                <div class="col-span-6 md:col-span-8">
                    <template v-for="(item, key) in collectionStore.colors">
                        <Link :href="route('catalog', {
                                                 category_title:collectionStore.collection.category.slug,
                                                 colors: JSON.stringify([item.id]),
                                                 })"
                              class="hover:text-navHoverColor transition underline underline-offset-4">
                            {{ item.title }}
                        </Link>
                        <span v-if="key < Object.keys(collectionStore.colors).length - 1"> | </span>
                    </template>
                </div>
            </template>
            <template v-if="collectionStore.rooms">
                <div class="col-span-6 md:col-span-4">
                    Помещения
                </div>
                <div class="col-span-6 md:col-span-8">
                    <template v-for="(item, key) in collectionStore.rooms">
                        <Link :href="route('catalog', {
                                                 category_title:collectionStore.collection.category.slug,
                                                 rooms: JSON.stringify([item.id]),
                                                 })"
                              class="hover:text-navHoverColor transition underline underline-offset-4">
                            {{ item.title }}
                        </Link>
                        <span v-if="key < Object.keys(collectionStore.rooms).length - 1"> | </span>
                    </template>
                </div>
            </template>
            <template v-if="collectionStore.patterns">
                <div class="col-span-6 md:col-span-4">
                    Рисунки
                </div>
                <div class="col-span-6 md:col-span-8">
                    <template v-for="(item, key) in collectionStore.patterns">
                        <Link :href="route('catalog', {
                                                 category_title:collectionStore.collection.category.slug,
                                                 patterns: JSON.stringify([item.id]),
                                                 })"
                              class="hover:text-navHoverColor transition underline underline-offset-4">
                            {{ item.title }}
                        </Link>
                        <span v-if="key < Object.keys(collectionStore.patterns).length - 1"> | </span>
                    </template>
                </div>
            </template>
        </div>
        <div v-else class="grid grid-cols-12 gap-3 text-xs font-medium text-gray-700 animate-pulse">
            <template v-for="i in 5" :key="i">
                <div class="col-span-6 md:col-span-4">
                    <div class="h-4 bg-gray-300 rounded w-24"></div>
                </div>
                <div class="col-span-6 md:col-span-8">
                    <div class="h-4 bg-gray-300 rounded w-full"></div>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
